// ===========================================
// CHANNELS (Build 4)
// ===========================================
// What each distributor is actually worth: revenue, volume, price realization
// ($/case — which swings a lot), and how much you've actually collected. All from
// their own orders — no cost/COGS assumptions. Deduction rate fills in once logged.

import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, ArrowUpDown, DollarSign, Check } from 'lucide-react';
import clsx from 'clsx';
import { wholesaleApi, restaurantApi, menuApi } from '../services/api';

const dollars = (c: number) => `$${Math.round((c || 0) / 100).toLocaleString('en-US')}`;
const per = (c: number, cases: number) => (cases > 0 ? `$${(c / 100 / cases).toFixed(2)}` : '—');

type Sort = 'revenue' | 'cases' | 'perCase' | 'collected' | 'margin';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function ChannelScorecard({ onError }: { onError?: (m: string) => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<Sort>('revenue');
  const [scope, setScope] = useState<'distributors' | 'all'>('distributors');
  // Channels = per-distributor; SKUs = the same sales cut by product instead —
  // Tassilo's "break down by SKU level" analysis.
  const [view, setView] = useState<'channels' | 'skus'>('channels');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCosts, setShowCosts] = useState(false);
  const [costDraft, setCostDraft] = useState<Record<string, string> | null>(null);

  const { data: orders = [] } = useQuery({ queryKey: ['wholesale-orders'], queryFn: () => wholesaleApi.getOrders().then((r) => r.data.data as any[]) });
  const { data: summary } = useQuery({ queryKey: ['wholesale-deduction-summary'], queryFn: () => wholesaleApi.getDeductionSummary().then((r) => r.data.data) });
  const { data: restaurant } = useQuery({ queryKey: ['restaurant-forecast'], queryFn: async () => (await restaurantApi.getCurrent()).data?.data || null });
  const { data: menuData } = useQuery({ queryKey: ['menu-items-flat'], queryFn: async () => { const res = await menuApi.getItems(); return res.data?.data || res.data || []; }, staleTime: 60_000 });
  const menuItems: Array<{ id: string; name: string }> = (Array.isArray(menuData) ? menuData : menuData?.items || []).filter((m: any) => m?.id);

  // Cost per case (their number, entered here) keyed by menuItemId → turns price into margin.
  const costPerCase: Record<string, number> = restaurant?.settings?.costPerCase || {};
  const hasCosts = Object.values(costPerCase).some((v) => v > 0);
  const saveCosts = useMutation({
    mutationFn: (map: Record<string, number>) => restaurantApi.updateSettings({ costPerCase: map }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['restaurant-forecast'] }); setCostDraft(null); setShowCosts(false); },
    onError: () => onError?.("Couldn't save the costs."),
  });
  // Last year's case volume per channel, from their master-sales file (keyed by lowercased name).
  const hist2025: Record<string, number> = restaurant?.settings?.salesHistory2025 || {};
  const has2025 = Object.keys(hist2025).length > 0;
  const cases2025For = (name: string) => {
    const n = name.toLowerCase();
    const key = Object.keys(hist2025).find((k) => n.includes(k) || k.includes(n.split(' ')[0]));
    return key ? hist2025[key] : null;
  };
  const dedByCust: Record<string, number> = {};
  for (const c of summary?.customers || []) dedByCust[c.customerId] = c.actualBps;

  // Per-channel forecast (fy + monthly cases), from their demand-forecast sheet.
  const chFc: Record<string, { fy: number; monthly: number[] }> = restaurant?.settings?.channelForecast || {};
  const hasPlan = Object.keys(chFc).length > 0;
  const planFor = (name: string) => {
    const n = name.toLowerCase();
    const key = Object.keys(chFc).find((k) => n.includes(k) || k.includes(n.split(' ')[0]));
    return key ? chFc[key] : null;
  };
  // Next year's plan per channel, when loaded — a forward-looking column, no attainment.
  const chFc27: Record<string, { fy: number; monthly: number[] }> = restaurant?.settings?.channelForecast2027 || {};
  const hasPlan27 = Object.keys(chFc27).length > 0;
  const plan27For = (name: string) => {
    const n = name.toLowerCase();
    const key = Object.keys(chFc27).find((k) => n.includes(k) || k.includes(n.split(' ')[0]));
    return key ? chFc27[key] : null;
  };

  // Price-per-case is only meaningful within a format — a 750ml case is not a 12-pack.
  const fmtOf = (n: string) => (n.includes('750') ? '750ml' : n.toLowerCase().includes('16ct') ? '16ct' : '12pk');
  const FMTS = ['12pk', '16ct', '750ml'] as const;

  // aggregate per distributor from their orders (cogs only where a cost per case is set).
  // Consignment shipments are stock parked at the distributor, not sales — excluded
  // from revenue/cases everywhere; they live on the Command Center's consignment card.
  // Revenue is recognized on DELIVERY (Tassilo's rule, Sep 4): open/in-transit
  // POs aren't sales yet; the month is the delivery month, not the PO month.
  const recognized = (o: any) => o.freightDelivered || o.invoiceSent || o.paymentReceived;
  const revenueDate = (o: any) => o.deliveryDate || o.fulfillmentDate;
  const byCust = new Map<string, { id: string; name: string; revenue: number; cases: number; collected: number; cogs: number; hasPO: boolean; monthly: number[]; fmt: Record<string, [number, number]> }>();
  for (const o of orders) {
    if (o.source === 'shopify' || o.status === 'cancelled' || o.onConsignment) continue;
    if (!recognized(o)) continue;
    // Depletion billing has a real dollar amount but no case count in the source —
    // it counts as revenue only, never as cases or price-per-case.
    const noCases = /case count n\/a/i.test(o.notes || '');
    const id = o.customerId; const name = o.customer?.name || '—';
    const row = byCust.get(id) || { id, name, revenue: 0, cases: 0, collected: 0, cogs: 0, hasPO: false, monthly: Array(12).fill(0), fmt: { '12pk': [0, 0], '16ct': [0, 0], '750ml': [0, 0] } };
    let orderCases = 0;
    for (const it of o.items || []) {
      if (noCases) continue;
      const qty = parseFloat(String(it.quantity)) || 0;
      row.cases += qty; orderCases += qty;
      row.cogs += qty * (costPerCase[it.menuItemId] || 0);
      const f = fmtOf(it.name || '');
      row.fmt[f][0] += it.totalPriceCents || 0; row.fmt[f][1] += qty;
    }
    if (revenueDate(o)) { const d = new Date(revenueDate(o)); if (d.getFullYear() === 2026) row.monthly[d.getMonth()] += orderCases; }
    if (o.poNumber) row.hasPO = true;
    // Freight is SIGNED per-PO: negative = we paid (a real cost), positive =
    // billed onward to the distributor (not a cost). Only the costs hit margin.
    row.cogs += Math.max(0, -(o.freightCents || 0));
    row.revenue += o.totalCents || 0;
    if (o.paymentReceived || o.status === 'paid') row.collected += o.totalCents || 0;
    byCust.set(id, row);
  }

  // Per-SKU cut over the same scope: which products actually earn.
  const bySku = new Map<string, { name: string; revenue: number; cases: number; cogs: number; monthly: number[] }>();
  for (const o of orders) {
    if (o.source === 'shopify' || o.status === 'cancelled' || o.onConsignment) continue;
    if (!recognized(o)) continue;
    if (/case count n\/a/i.test(o.notes || '')) continue;
    for (const it of o.items || []) {
      const qty = parseFloat(String(it.quantity)) || 0;
      if (!qty) continue;
      const row = bySku.get(it.menuItemId || it.name) || { name: it.name, revenue: 0, cases: 0, cogs: 0, monthly: Array(12).fill(0) };
      row.revenue += it.totalPriceCents || 0;
      row.cases += qty;
      row.cogs += qty * (costPerCase[it.menuItemId] || 0);
      if (revenueDate(o)) { const d = new Date(revenueDate(o)); if (d.getFullYear() === 2026) row.monthly[d.getMonth()] += qty; }
      bySku.set(it.menuItemId || it.name, row);
    }
  }
  const skuRows = [...bySku.values()].filter((r) => r.revenue > 0).sort((a, b) => b.revenue - a.revenue);

  // Accounts with no PO-based orders (e.g. restaurants) get everything — the
  // Distributors default only applies when there are distributors to show.
  const anyPO = [...byCust.values()].some((r) => r.hasPO);
  const effScope = anyPO ? scope : 'all';
  const needle = q.trim().toLowerCase();
  let rows = [...byCust.values()].filter((r) =>
    r.revenue > 0 && (effScope === 'all' || r.hasPO) && (!needle || r.name.toLowerCase().includes(needle)));
  const fmtPrice = (r: any, f: string) => (r.fmt[f][1] > 0 ? r.fmt[f][0] / r.fmt[f][1] : 0);
  rows.sort((a, b) => {
    if (sort === 'cases') return b.cases - a.cases;
    if (sort === 'perCase') return fmtPrice(b, '12pk') - fmtPrice(a, '12pk'); // compare within one format only
    if (sort === 'collected') return (b.collected / (b.revenue || 1)) - (a.collected / (a.revenue || 1));
    if (sort === 'margin') return ((b.revenue - b.cogs) / (b.revenue || 1)) - ((a.revenue - a.cogs) / (a.revenue || 1));
    return b.revenue - a.revenue;
  });

  const totalRev = rows.reduce((s, r) => s + r.revenue, 0);
  const totalCases = rows.reduce((s, r) => s + r.cases, 0);
  const totalCollected = rows.reduce((s, r) => s + r.collected, 0);
  const anyDeductions = Object.values(dedByCust).some((v) => v > 0);

  const Th = ({ k, label, right }: { k: Sort; label: string; right?: boolean }) => (
    <th className={clsx('px-4 py-2 font-medium', right && 'text-right')}>
      <button onClick={() => setSort(k)} className={clsx('inline-flex items-center gap-1 hover:text-slate-700', sort === k ? 'text-slate-700' : '')}>
        {label}<ArrowUpDown className="w-3 h-3 opacity-40" />
      </button>
    </th>
  );

  return (
    <div className="space-y-4">
      <div className={clsx('grid gap-2', hasCosts ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3')}>
        <div className="card p-3"><p className="text-xs text-slate-500">Revenue ({effScope === 'distributors' ? 'distributors' : 'all accounts'})</p><p className="text-2xl font-bold text-slate-900">{dollars(totalRev)}</p></div>
        {(() => { // price per case, by format — a blended average across formats is meaningless
          const tf: Record<string, [number, number]> = { '12pk': [0, 0], '16ct': [0, 0], '750ml': [0, 0] };
          for (const r of rows) for (const f of FMTS) { tf[f][0] += r.fmt[f][0]; tf[f][1] += r.fmt[f][1]; }
          return (
            <div className="card p-3">
              <p className="text-xs text-slate-500">Price per case, by format</p>
              <div className="text-sm font-bold text-slate-900 leading-6">
                {FMTS.filter((f) => tf[f][1] > 0).map((f) => (
                  <span key={f} className="mr-2 whitespace-nowrap">{f} {per(tf[f][0], tf[f][1])}</span>
                ))}
              </div>
              <p className="text-xs text-slate-400">{Math.round(totalCases).toLocaleString('en-US')} cases</p>
            </div>
          );
        })()}
        <div className="card p-3"><p className="text-xs text-slate-500">Collected</p><p className="text-2xl font-bold text-emerald-600">{totalRev ? Math.round((totalCollected / totalRev) * 100) : 0}%</p><p className="text-xs text-slate-400">{dollars(totalCollected)} of {dollars(totalRev)}</p></div>
        {hasCosts && (() => { const totalCogs = rows.reduce((s, r) => s + r.cogs, 0); const m = totalRev - totalCogs; const mp = totalRev ? Math.round((m / totalRev) * 100) : 0;
          return <div className="card p-3"><p className="text-xs text-slate-500">Margin</p><p className="text-2xl font-bold text-slate-900">{dollars(m)}</p><p className="text-xs text-slate-400">{mp}% of revenue</p></div>; })()}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a channel…" className="w-full text-sm rounded-xl border border-slate-200 pl-9 pr-3 py-2.5 focus:ring-2 focus:ring-primary-400 focus:border-primary-400" />
        </div>
        {anyPO && (
          <div className="flex rounded-lg bg-slate-100 p-0.5 text-xs shrink-0">
            <button onClick={() => setScope('distributors')} className={effScope === 'distributors' ? 'px-2.5 py-1.5 rounded-md bg-white shadow-sm font-medium' : 'px-2.5 py-1.5 text-slate-500'}>Distributors</button>
            <button onClick={() => setScope('all')} className={effScope === 'all' ? 'px-2.5 py-1.5 rounded-md bg-white shadow-sm font-medium' : 'px-2.5 py-1.5 text-slate-500'}>All accounts</button>
          </div>
        )}
        <div className="flex rounded-lg bg-slate-100 p-0.5 text-sm">
          <button onClick={() => setView('channels')} className={view === 'channels' ? 'px-2.5 py-1.5 rounded-md bg-white shadow-sm font-medium' : 'px-2.5 py-1.5 text-slate-500'}>By channel</button>
          <button onClick={() => setView('skus')} className={view === 'skus' ? 'px-2.5 py-1.5 rounded-md bg-white shadow-sm font-medium' : 'px-2.5 py-1.5 text-slate-500'}>By SKU</button>
        </div>
      </div>

      {view === 'skus' && (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                <th className="px-4 py-2 font-medium">Product</th>
                <th className="px-4 py-2 font-medium text-right">Revenue</th>
                <th className="px-4 py-2 font-medium text-right">Cases</th>
                <th className="px-4 py-2 font-medium text-right">$/case</th>
                {hasCosts && <th className="px-4 py-2 font-medium text-right">Margin</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {skuRows.map((r) => {
                const m = r.revenue - r.cogs;
                const mp = r.revenue ? Math.round((m / r.revenue) * 100) : 0;
                return (
                  <tr key={r.name}>
                    <td className="px-4 py-2.5 font-medium text-slate-900">{r.name}</td>
                    <td className="px-4 py-2.5 text-right text-slate-700">{dollars(r.revenue)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{Math.round(r.cases).toLocaleString('en-US')}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{per(r.revenue, r.cases)}</td>
                    {hasCosts && <td className="px-4 py-2.5 text-right"><span className="font-medium text-slate-900">{dollars(m)}</span> <span className={clsx('text-xs', mp >= 30 ? 'text-emerald-600' : mp >= 15 ? 'text-amber-600' : 'text-rose-600')}>({mp}%)</span></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[11px] text-slate-400 px-4 py-2">Same sales, cut by product. Margin here = revenue − cost per case (freight belongs to POs, not SKUs).</p>
        </div>
      )}

      {view === 'channels' && (
      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
              <th className="px-4 py-2 font-medium">Channel</th>
              <Th k="revenue" label="Revenue" right />
              <Th k="cases" label="Cases" right />
              {has2025 && <th className="px-4 py-2 font-medium text-right">vs 2025</th>}
              {hasPlan && <th className="px-4 py-2 font-medium text-right">Plan</th>}
              {hasPlan27 && <th className="px-4 py-2 font-medium text-right">2027 plan</th>}
              <Th k="perCase" label="$/case" right />
              <Th k="collected" label="Collected" right />
              {hasCosts && <Th k="margin" label="Margin" right />}
              {anyDeductions && <th className="px-4 py-2 font-medium text-right">Deductions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map((r) => {
              const collectedPct = r.revenue ? Math.round((r.collected / r.revenue) * 100) : 0;
              const bps = dedByCust[r.id];
              const plan = hasPlan ? planFor(r.name) : null;
              const plan27 = hasPlan27 ? plan27For(r.name) : null;
              const colCount = 5 + (has2025 ? 1 : 0) + (hasPlan ? 1 : 0) + (hasPlan27 ? 1 : 0) + (hasCosts ? 1 : 0) + (anyDeductions ? 1 : 0);
              const isOpen = expandedId === r.id;
              return (
                <Fragment key={r.id}>
                <tr onClick={() => setExpandedId(isOpen ? null : r.id)} className="cursor-pointer hover:bg-slate-50/60" title="Click for month-by-month">
                  <td className="px-4 py-2.5 font-medium text-slate-900">{r.name}</td>
                  <td className="px-4 py-2.5 text-right text-slate-600">{dollars(r.revenue)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-600">{Math.round(r.cases).toLocaleString('en-US')}</td>
                  {has2025 && (() => {
                    const py = cases2025For(r.name);
                    if (py == null || py === 0) return <td className="px-4 py-2.5 text-right text-slate-300">—</td>;
                    const g = Math.round((r.cases / py - 1) * 100);
                    return <td className={clsx('px-4 py-2.5 text-right text-xs', g >= 0 ? 'text-emerald-600' : 'text-rose-600')}>{py.toLocaleString('en-US')} <span className="text-slate-400">({g >= 0 ? '+' : ''}{g}%)</span></td>;
                  })()}
                  {hasPlan && (() => {
                    if (!plan || !plan.fy) return <td className="px-4 py-2.5 text-right text-slate-300">—</td>;
                    const att = Math.round((r.cases / plan.fy) * 100);
                    return <td className="px-4 py-2.5 text-right text-xs text-slate-600">{plan.fy.toLocaleString('en-US')} <span className={clsx(att >= 60 ? 'text-emerald-600' : att >= 40 ? 'text-amber-600' : 'text-rose-600')}>({att}%)</span></td>;
                  })()}
                  {hasPlan27 && (() => {
                    if (!plan27 || !plan27.fy) return <td className="px-4 py-2.5 text-right text-slate-300">—</td>;
                    // vs this year's plan, so growth ambition is visible per channel
                    const g = plan?.fy ? Math.round((plan27.fy / plan.fy - 1) * 100) : null;
                    return <td className="px-4 py-2.5 text-right text-xs text-slate-600">{Math.round(plan27.fy).toLocaleString('en-US')}{g != null && <span className="text-slate-400"> ({g >= 0 ? '+' : ''}{g}%)</span>}</td>;
                  })()}
                  <td className="px-3 py-2.5 text-right font-medium text-slate-900 text-xs">
                    {r.cases === 0 ? <span className="text-slate-300">—</span> :
                      FMTS.filter((f) => r.fmt[f][1] > 0).map((f, i) => (
                        <span key={f} className="whitespace-nowrap">{i > 0 && <span className="text-slate-300"> · </span>}{f} {per(r.fmt[f][0], r.fmt[f][1])}</span>
                      ))}
                  </td>
                  <td className="px-4 py-2.5 text-right"><span className={clsx(collectedPct >= 90 ? 'text-emerald-600' : collectedPct >= 50 ? 'text-amber-600' : 'text-rose-600')}>{collectedPct}%</span></td>
                  {hasCosts && (() => {
                    const marginPct = r.revenue ? Math.round(((r.revenue - r.cogs) / r.revenue) * 100) : 0;
                    return <td className="px-4 py-2.5 text-right"><span className="font-medium text-slate-900">{dollars(r.revenue - r.cogs)}</span> <span className={clsx('text-xs', marginPct >= 30 ? 'text-emerald-600' : marginPct >= 15 ? 'text-amber-600' : 'text-rose-600')}>({marginPct}%)</span></td>;
                  })()}
                  {anyDeductions && <td className="px-4 py-2.5 text-right text-slate-600">{bps ? `${(bps / 100).toFixed(1)}%` : '—'}</td>}
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={colCount} className="px-4 pb-3 pt-1 bg-slate-50/50">
                      <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5">
                        {MONTHS.map((m, i) => {
                          const act = Math.round(r.monthly[i]); const pl = plan?.monthly?.[i] ?? null;
                          return (
                            <div key={m} className="rounded-lg bg-white border border-slate-100 px-1.5 py-1 text-center">
                              <p className="text-[10px] text-slate-400">{m}</p>
                              <p className="text-xs font-semibold text-slate-900">{act ? act.toLocaleString('en-US') : '—'}</p>
                              {pl != null && <p className="text-[10px] text-slate-400">of {pl.toLocaleString('en-US')}</p>}
                            </div>
                          );
                        })}
                      </div>
                      {plan27?.monthly?.some((v) => v) && (
                        <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5 mt-1.5">
                          {MONTHS.map((m, i) => (
                            <div key={m} className="rounded-lg bg-blue-50/60 border border-blue-100 px-1.5 py-1 text-center">
                              <p className="text-[10px] text-blue-400">{m} ’27</p>
                              <p className="text-xs font-semibold text-blue-900">{plan27.monthly[i] ? Math.round(plan27.monthly[i]).toLocaleString('en-US') : '—'}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-[11px] text-slate-400 mt-1.5">Cases delivered each month{plan ? ' vs your forecast' : ''}{plan27?.monthly?.some((v) => v) ? '; blue row = next year\'s plan' : ''}.</p>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {/* Cost per case → margin */}
      <div className="card p-4">
        {!showCosts ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-slate-500">
              {hasCosts ? 'Margin = revenue − your cost per case − freight you paid (negative freight on a PO; positive = billed onward, not a cost).' : 'Add your cost per case to turn price into profit per channel.'}
            </p>
            <button onClick={() => { setCostDraft(Object.fromEntries(menuItems.map((m) => [m.id, costPerCase[m.id] ? (costPerCase[m.id] / 100).toFixed(2) : '']))); setShowCosts(true); }}
              className="btn btn-sm btn-secondary flex items-center gap-1.5 shrink-0"><DollarSign className="w-3.5 h-3.5" /> {hasCosts ? 'Edit costs' : 'Set cost per case'}</button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Your cost to make one case</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {menuItems.map((m) => (
                <label key={m.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-slate-600 truncate">{m.name}</span>
                  <div className="relative shrink-0">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                    <input type="number" step="0.01" value={costDraft?.[m.id] ?? ''} onChange={(e) => setCostDraft({ ...(costDraft || {}), [m.id]: e.target.value })}
                      className="w-24 text-sm text-right rounded-lg border border-slate-200 pl-5 pr-2 py-1.5 focus:ring-2 focus:ring-primary-400" placeholder="—" />
                  </div>
                </label>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => { const map: Record<string, number> = {}; for (const [id, v] of Object.entries(costDraft || {})) { const c = Math.round(parseFloat(v) * 100); if (c > 0) map[id] = c; } saveCosts.mutate(map); }}
                disabled={saveCosts.isPending} className="btn btn-sm btn-primary flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Save</button>
              <button onClick={() => { setShowCosts(false); setCostDraft(null); }} className="btn btn-sm btn-secondary">Cancel</button>
            </div>
            <p className="text-xs text-slate-400">Only what you enter is used — nothing is assumed. Costs stay private to your account.</p>
          </div>
        )}
      </div>
    </div>
  );
}
